import React, { useState, useMemo } from 'react';
import { Button, Modal } from 'ui';
import { useCurrentUser } from 'hooks/users/queries';
import { useAcceptTermsOfService } from 'hooks/users/mutations';
import { useSignOut } from 'hooks/auth/mutations';
import TwoOptionModal from './TwoOptionModal';

const ModalState = {
  NORMAL: "NORMAL",
  LOADING: "LOADING",
  DECLINED: "DECLINED",
};

function TermsOfServiceModal() {
  const [modalState, setModalState] = useState(ModalState.NORMAL);
  
  // Use React Query to fetch current user data
  const { data: user, isLoading } = useCurrentUser();
  
  // Mutation for accepting terms
  const acceptTermsMutation = useAcceptTermsOfService();
  
  // Mutation for signing out
  const signOut = useSignOut();
  
  // Calculate if modal should show based on user data
  const show = useMemo(() => {
    if (isLoading || !user) return false;
    
    // Check if user has accepted terms
    const acceptedTermsAt = user.accepted_terms_at;
    const mostRecentTerms = user.most_recent_terms;
    
    if (!acceptedTermsAt) return true;
    if (!mostRecentTerms) return false;
    
    return acceptedTermsAt < mostRecentTerms;
  }, [user, isLoading]);
  
  const handleSignOut = () => {
    // Sign out the user
    signOut.mutate();
  };
  
  const handleAcceptTerms = () => {
    setModalState(ModalState.LOADING);
    acceptTermsMutation.mutate(undefined, {
      onSuccess: () => {
        setModalState(ModalState.NORMAL);
      },
      onError: () => {
        setModalState(ModalState.NORMAL);
      }
    });
  };
  
  const handleRejectTerms = () => {
    setModalState(ModalState.DECLINED);
  };
  
  // Don't show modal if still loading user data
  if (isLoading || !show) {
    return null;
  }
  
  if (modalState === ModalState.DECLINED) {
    return (
      <div>
        <Modal show={true}>
          <Modal.Header>
            <Modal.Title>Terms and Conditions</Modal.Title>
          </Modal.Header>
          
          <Modal.Body>
            <p>In order to continue using Delve, you will need to accept our <a href="https://delvetool.com/terms" target="_blank">Terms of Service</a> and <a href="https://delvetool.com/privacy-policy" target="_blank">Privacy Policy</a>.</p>
            <p>
              If you have any questions or would like to discuss the agreements,
              please send us an email at <a href="mailto:team@twentytonine.com">
                team@twentytonine.com
              </a>.
            </p>
          </Modal.Body>
          
          <Modal.Footer>
            <Button onClick={handleSignOut} variant="blue">Okay</Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
  
  return (
    <TwoOptionModal
      show={true}
      title="Terms and Conditions"
      onNo={handleRejectTerms}
      onYes={handleAcceptTerms}
      noText="I Decline"
      yesText="I Agree"
    >
      We have updated our <a href="https://delvetool.com/terms" target="_blank">Delve Terms of Service</a> and <a href="https://delvetool.com/privacy-policy" target="_blank">User Privacy Policy</a>. To continue you must read and agree to the updated agreements.
    </TwoOptionModal>
  );
}

export default TermsOfServiceModal;